import { eHierarchyLevel } from "../../functions/api/enums";
import { HierarchyContextProps, IHierarchyActionLoadClinicalLibrary } from "./interface";
import { mergeClinicalLibraryData } from "./mergeClinicalLibraryData";

const templateLevels: (eHierarchyLevel | undefined)[] = [
  eHierarchyLevel.TopLevel,
  eHierarchyLevel.Corporate,
  eHierarchyLevel.TA,
  eHierarchyLevel.Project,
];

/**
 * Determines whether a hierarchy level is a template or shell.
 *
 * @param level - The hierarchy level to check
 * @returns "Template" if the level is a template level, otherwise "Shell"
 */
const levelType = (level: eHierarchyLevel): "Template" | "Shell" => {
  return templateLevels.includes(level) ? "Template" : "Shell";
};

/**
 * Reducer function to load clinical library data for a specific hierarchy.
 * Updates the hierarchy node with the clinical library and rebuilds parent templates.
 *
 * @param action - Action containing the hierarchy ID and clinical library data
 * @param state - Current hierarchy context state
 * @returns Updated hierarchy context state
 */
export const hcr_loadClinicalLibrary = (
  action: IHierarchyActionLoadClinicalLibrary,
  state: HierarchyContextProps,
): HierarchyContextProps => {
  if (!action.clinicalLibrary || action.clinicalLibrary.length === 0) {
    return state;
  }

  // Find the hierarchy nodes to update
  const updatedHierarchyNodes = state.hierarchyNodes.map((hn) => {
    const matchingLibrary = action.clinicalLibrary.find((cl) => cl.hierarchyId === hn.id);
    if (matchingLibrary) {
      return {
        ...hn,
        clinicalLibrary: matchingLibrary,
      };
    }
    return hn;
  });

  // If current hierarchy is being updated, update it as well
  const matchingHierarchyLibrary = state.hierarchy
    ? action.clinicalLibrary.find((cl) => cl.hierarchyId === state.hierarchy?.id)
    : undefined;

  const updatedHierarchy =
    state.hierarchy && matchingHierarchyLibrary
      ? {
          ...state.hierarchy,
          clinicalLibrary: matchingHierarchyLibrary,
        }
      : state.hierarchy;

  if (!state.hierarchy) {
    // If no current hierarchy, just update the nodes
    return {
      ...state,
      hierarchyNodes: updatedHierarchyNodes,
      hierarchy: updatedHierarchy,
    };
  }

  // Rebuild parent templates by iterating through the clinical library collection
  // Filter to only include libraries from hierarchies with matching level type
  const relevantLibraries = action.clinicalLibrary.filter((cl) => {
    const node = updatedHierarchyNodes.find((n) => n.id === cl.hierarchyId);
    return node && levelType(state.hierarchy!.level) === levelType(node.level);
  });

  // Merge clinical library data into parent templates using reduce pattern from hcr_loadHierarchy
  const clinicalTemplates = relevantLibraries.reduce(
    (acc, clinicalLibrary) => mergeClinicalLibraryData(acc, clinicalLibrary),
    {
      clinicalDatasets: [...state.parentTemplates.clinicalDatasets],
      clinicalCodeLists: [...state.parentTemplates.clinicalCodeLists],
      clinicalValueLists: [...state.parentTemplates.clinicalValueLists],
      clinicalComments: [...state.parentTemplates.clinicalComments],
      clinicalMethods: [...state.parentTemplates.clinicalMethods],
      clinicalWhereClauses: [...state.parentTemplates.clinicalWhereClauses],
    },
  );

  const newParentTemplates = {
    ...clinicalTemplates,
    domainReplacements: [...state.parentTemplates.domainReplacements],
    lists: [...state.parentTemplates.lists],
    rowGroups: [...state.parentTemplates.rowGroups],
    singles: [...state.parentTemplates.singles],
  };

  return {
    ...state,
    hierarchyNodes: updatedHierarchyNodes,
    hierarchy: updatedHierarchy,
    parentTemplates: newParentTemplates,
  };
};
